/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Send } from 'lucide-react';
import React, { useRef, useEffect, useState, useCallback } from 'react';

import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface ChatInputProps {
    inputValue: string;
    isLoading: boolean;
    isConnected: boolean;
    onInputChange: (value: string) => void;
    onSendMessage: () => void;
}

export const ChatInput = ({ inputValue, isLoading, isConnected, onInputChange, onSendMessage }: ChatInputProps) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<number>(200);

    // Calculate 25% of the chat container height
    const updateMaxHeight = useCallback(() => {
        if (containerRef.current) {
            // Find the parent chat container by traversing up the DOM
            let chatContainer = containerRef.current.parentElement;
            while (chatContainer && !chatContainer.classList.contains('flex-1')) {
                chatContainer = chatContainer.parentElement;
            }

            if (chatContainer) {
                const containerHeight = chatContainer.clientHeight;
                const newMaxHeight = Math.floor(containerHeight * 0.25);
                // Ensure minimum height of 60px and maximum reasonable height
                setMaxHeight(Math.max(60, Math.min(newMaxHeight, 300)));
            }
        }
    }, []);

    // Update max height on mount and window resize
    useEffect(() => {
        updateMaxHeight();

        const handleResize = () => {
            updateMaxHeight();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateMaxHeight]);

    // Auto-resize textarea based on content
    const adjustTextareaHeight = useCallback(() => {
        if (inputRef.current) {
            // Reset height to auto to get the correct scrollHeight
            inputRef.current.style.height = 'auto';

            // Calculate the new height based on scroll height
            const scrollHeight = inputRef.current.scrollHeight;
            const newHeight = Math.min(scrollHeight, maxHeight);

            inputRef.current.style.height = `${newHeight}px`;

            // Enable/disable scrolling based on whether content exceeds max height
            if (scrollHeight > maxHeight) {
                inputRef.current.style.overflowY = 'auto';
            } else {
                inputRef.current.style.overflowY = 'hidden';
            }
        }
    }, [maxHeight]);

    // Adjust height when input value changes
    useEffect(() => {
        adjustTextareaHeight();
    }, [inputValue, adjustTextareaHeight]);

    // Focus input when not loading
    useEffect(() => {
        if (!isLoading) {
            inputRef.current?.focus();
        }
    }, [isLoading]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onInputChange(e.target.value);
    };

    return (
        <div ref={containerRef} className="p-4 flex-shrink-0">
            <div className="flex gap-2 min-w-0 items-end">
                <Textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Describe what you want to build or modify..."
                    disabled={isLoading || !isConnected}
                    className="flex-1 min-w-0 min-h-[36px] resize-none"
                    rows={1}
                    style={{
                        maxHeight: `${maxHeight}px`,
                    }}
                />
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={onSendMessage}
                                disabled={!inputValue.trim() || isLoading || !isConnected}
                                size="icon"
                                className="h-10 w-10 flex-shrink-0 bg-purple-700 hover:bg-purple-800
                                disabled:bg-purple-500"
                            >
                                <Send className="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Send message (Enter)</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </div>
    );
};
